import React, {useState} from "react";
import {User} from "../../../../api/accessControl/User.ts";
import {BackendApi} from "../../../../api/api.ts";
import Shell from "../../../../components/specific/Shell.tsx";
import {Tab, Tabs} from "../../../../components/general/Tabs.tsx";
import {BriefcaseIcon, GlobeAltIcon, UserCircleIcon} from "@heroicons/react/24/outline";
import {EditableSettingRow} from "../../../../components/form/EditableSettingRow.tsx";
import {useNotifications} from "../../../../lib/NotificationContext.tsx";
import {useAdmin__partner__demo} from "../../../../generated/blueprint.ts";
import {QueryKey} from "../../../../api/queryKeys.ts";


interface PartnerData {
    id: string;
    name: string;
    slug: string;
    tagline: string;
    address: string;
    email: string;
    phone_number: string;
    primary_color: string;
    full_logo_url: string;
    icon_logo_url: string;
    website_url: string;
    business_id: string;
}

function SettingRow({ name, children, full }: { name: string, children: any, full?: boolean }) {
    return (
        <div className={`justify-between bg-gray-50 p-3 rounded-md ${!full ? 'sm:flex' : ''}`}>
            <dt className="font-medium text-gray-900 sm:w-64 sm:pr-6 flex items-center gap-2">
                {name}
            </dt>
            <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0">
                {children}
            </dd>
        </div>
    )
}

export default function DemoPartnerAdminPage({ currentUser }: { currentUser: User }) {
    const {dispatch} = useNotifications();
    const api = new BackendApi(currentUser.getToken());

    const { data: partner, isLoading, refetch } = useAdmin__partner__demo<PartnerData | null>(
        api,
        {queryKey: QueryKey.PartnerDemo, select: (data) => data.partner || null},
    );

    const [selectedColor, setSelectedColor] = useState<string>("");

    React.useEffect(() => {
        if (partner?.primary_color) {
            setSelectedColor(partner.primary_color.replace('#', ''));
        }
    }, [partner]);

    const handleUpdateField = async (field: keyof PartnerData, value: string) => {
        if (!partner) return;
        try {
            await api.Admin__partner__demo__configure({
                ...partner,
                [field]: value,
            })

            await refetch();

        } catch (error) {
            dispatch({
                title: 'Fout bij bijwerken',
                description: 'Er is iets misgegaan bij het opslaan van de wijziging.',
                type: 'error'
            });
            throw error;
        }
    };



    return (
        <Shell currentUser={currentUser} isLoading={isLoading}>
            <h1 className="weight-600 text-3xl">Partner Instellingen</h1>
            <p className="text-gray-500 mt-2">Configureer de branding en gegevens van de demo-omgeving.</p>

            <div className="mt-12 max-w-5xl">
                <Tabs>
                    <Tab label="Identiteit" icon={UserCircleIcon}>
                        <dl className="space-y-3 text-sm leading-6 mt-6">
                            <h3 className="text-xl font-gray-900 py-3 pt-6">Branding & Informatie</h3>
                            <EditableSettingRow
                                name="Naam"
                                value={partner?.name}
                                onSave={(val) => handleUpdateField('name', val)}
                                placeholder="Partner Naam"
                            />
                            <EditableSettingRow
                                name="Tagline"
                                value={partner?.tagline}
                                onSave={(val) => handleUpdateField('tagline', val)}
                                placeholder="Tagline"
                            />

                            <SettingRow name="Primaire Kleur">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="color"
                                        value={selectedColor ? `#${selectedColor}` : "#000000"}
                                        onChange={(e) => setSelectedColor(e.target.value.replace('#', ''))}
                                        className="h-10 w-20 cursor-pointer rounded border border-gray-200 bg-white p-1"
                                    />
                                    <span className="text-gray-500 font-mono text-sm leading-6">
                                        #{selectedColor}
                                    </span>
                                    {selectedColor !== partner?.primary_color?.replace('#', '') && (
                                        <button
                                            onClick={async () => {
                                                await handleUpdateField('primary_color', selectedColor);
                                                await refetch();
                                            }}
                                            className="ml-2 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                                        >
                                            Opslaan
                                        </button>
                                    )}
                                </div>
                            </SettingRow>
                        </dl>
                    </Tab>
                    <Tab label="Asset URL's" icon={GlobeAltIcon}>
                        <dl className="space-y-3 text-sm leading-6 mt-6">
                            <h3 className="text-xl font-gray-900 py-3 pt-6">Asset Koppelingen</h3>
                            <EditableSettingRow
                                name="Volledig Logo URL"
                                value={partner?.full_logo_url}
                                onSave={(val) => handleUpdateField('full_logo_url', val)}
                                placeholder="https://..."
                            />
                            <EditableSettingRow
                                name="Icoon Logo URL"
                                value={partner?.icon_logo_url}
                                onSave={(val) => handleUpdateField('icon_logo_url', val)}
                                placeholder="https://..."
                            />
                            <EditableSettingRow
                                name="Website URL"
                                value={partner?.website_url}
                                onSave={(val) => handleUpdateField('website_url', val)}
                                placeholder="https://..."
                            />
                        </dl>
                    </Tab>
                    <Tab label="Contact & Meta" icon={BriefcaseIcon}>
                        <dl className="space-y-3 text-sm leading-6 mt-6">
                            <h3 className="text-xl font-gray-900 py-3 pt-6">Contactgegevens</h3>
                            <EditableSettingRow
                                name="Email"
                                value={partner?.email}
                                onSave={(val) => handleUpdateField('email', val)}
                                placeholder="support@demo.com"
                            />
                            <EditableSettingRow
                                name="Telefoonnummer"
                                value={partner?.phone_number}
                                onSave={(val) => handleUpdateField('phone_number', val)}
                                placeholder="06..."
                            />
                            <EditableSettingRow
                                name="Adres"
                                value={partner?.address}
                                onSave={(val) => handleUpdateField('address', val)}
                                placeholder="Straatnaam 123, 1234 AB Woonplaats"
                            />
                        </dl>
                        <dl className="space-y-3 text-sm leading-6 mt-12 bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-lg font-bold text-gray-700">Technische Metadata (Alleen-lezen)</h3>
                            <SettingRow name="Slug">
                                <span className="font-mono text-xs bg-white px-2 py-1 rounded border border-gray-200">{partner?.slug}</span>
                            </SettingRow>
                            <SettingRow name="Business ID">
                                <span className="font-mono text-xs text-gray-500">{partner?.business_id}</span>
                            </SettingRow>
                            <SettingRow name="Interne ID">
                                <span className="font-mono text-xs text-gray-500">{partner?.id}</span>
                            </SettingRow>
                        </dl>
                    </Tab>
                </Tabs>
            </div>
        </Shell>
    );
}
